import React from "react";

const REPORT_SECTIONS = [
  { key: "lungsAndAirways", label: "Lungs and airways" },
  { key: "pleura", label: "Pleura" },
  { key: "abdomen", label: "Abdomen" },
  { key: "bones", label: "Bones" },
  { key: "heartAndBloodVessels", label: "Heart and blood vessels" },
  { key: "lymphNodules", label: "Lymph nodules" },
  { key: "lungNodules", label: "Lung Nodules" },
  { key: "impression", label: "Impression" },
];

// helpers
function formatDate(rawDate) {
  if (!rawDate || String(rawDate).length !== 8) return "N/A";
  const y = String(rawDate).slice(0, 4);
  const m = String(rawDate).slice(4, 6);
  const d = String(rawDate).slice(6, 8);
  return `${d}/${m}/${y}`;
}
function toFixedOrNA(v, d = 1) {
  return typeof v === "number" ? v.toFixed(d) : "N/A";
}
function fmt(v) {
  return typeof v === "number" ? v.toFixed(1) : "N/A";
}

const PDFPreview = ({ reportData }) => {
  // ---- Exam meta (safe defaults) ----
  const {
    patientID = "",
    patientName = "",
    patientSex = "",
    patientBirthDate = "",
    studyDate = "",
    mainNoduleSummary = "",
  } = reportData?.examMeta || {};

  // ---- Findings (from annotationsData) ----
  const annotationsData = reportData?.annotationsData;
  const genesisFindings = Object.values(annotationsData || {})
    .flat()
    .filter((a) => a.creator === "Genesis AI" && a.type === "finding");

  // ---- Snapshots (pre-rendered CT+overlay) ----
  const snapshots = reportData?.snapshots || [];
  const isGenerating = !!reportData?.isGeneratingSnapshots;
  const snapshotErrors = reportData?.snapshotErrors || [];

  // ---- Report fields (object with the 8 fixed keys) ----
  const reportFields =
    reportData?.reportFields ?? reportData?.reportFileds ?? {};

  return (
    <div
      className="bg-white text-black font-sans text-sm"
      style={{ boxSizing: "border-box", padding: "40px" }}>
      {/* HEADER SECTION - Keep title, patient info, and main nodule together */}
      <div data-pdf-block>
        <h1 className="text-xl font-bold mb-3">Study Report</h1>

        {/* Patient Info */}
        <div className="border border-black">
          <div className="bg-[#D9E7F4] font-semibold p-2 border-b border-black">
            PATIENT INFORMATION
          </div>
          <div className="grid grid-cols-5 text-xs p-2">
            <div>
              Patient ID
              <br />
              {patientID}
            </div>
            <div>
              Name
              <br />
              {patientName}
            </div>
            <div>
              Sex
              <br />
              {patientSex}
            </div>
            <div>
              Birth Date
              <br />
              {formatDate(patientBirthDate)}
            </div>
            <div>
              Study Date
              <br />
              {formatDate(studyDate)}
            </div>
          </div>
        </div>

        {/* Main Nodule Summary */}
        <div className="border border-black mt-4">
          <div className="bg-[#D9E7F4] font-semibold p-2 border-b border-black">
            RECOMMENDED MANAGEMENT BY MAIN NODULE
          </div>
          <div className="p-4 text-sm">
            {mainNoduleSummary || "No main nodule summary available."}
          </div>
        </div>
      </div>

      {/* FINDINGS SECTION - Wrapper with header and subblocks */}
      {genesisFindings.length > 0 && (
        <div className="border border-black mt-4" data-pdf-block>
          {/* Header that will repeat if section splits */}
          <div
            className="bg-[#D9E7F4] font-semibold p-2 border-b border-black"
            data-pdf-header>
            FINDINGS
          </div>

          {/* Container for findings with proper padding */}
          <div className="p-4" style={{ padding: "16px" }}>
            {genesisFindings.map((nodule, index) => (
              <div
                key={nodule.id || index}
                className="text-xs mb-6 border border-black/20 rounded overflow-hidden"
                data-pdf-subblock>
                {/* Header Row */}
                <div className="grid grid-cols-6 text-white text-xs font-bold bg-[#1D4A72]">
                  <div
                    className="flex items-center justify-center border-r border-white py-2"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
                    NODULE
                  </div>
                  <div
                    className="flex items-center justify-center border-r border-white py-2"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
                    DIAMETER
                  </div>
                  <div
                    className="flex items-center justify-center border-r border-white py-2"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
                    VOLUME
                  </div>
                  <div
                    className="flex items-center justify-center border-r border-white py-2"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
                    SLICE LOCATION
                  </div>
                  <div
                    className="flex items-center justify-center border-r border-white py-2"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
                    LOCATION
                  </div>
                  <div
                    className="flex items-center justify-center py-2"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
                    NODULE TYPE
                  </div>
                </div>

                {/* Values */}
                <div
                  className="grid grid-cols-6 gap-x-4 px-2 py-2 text-xs"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
                  }}>
                  <div
                    className="text-left flex items-center"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      minHeight: "24px",
                    }}>
                    {index + 1}
                  </div>
                  <div
                    className="text-left flex items-center"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      minHeight: "24px",
                    }}>
                    {toFixedOrNA(nodule.grain?.size?.mm, 1)}mm
                  </div>
                  <div
                    className="text-left flex items-center"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      minHeight: "24px",
                    }}>
                    {toFixedOrNA(nodule.grain?.size?.mm3, 1)}mm
                    <sup>3</sup>
                  </div>
                  <div
                    className="text-left flex items-center"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      minHeight: "24px",
                    }}>
                    {nodule.slice || "N/A"}
                  </div>
                  <div
                    className="text-left flex items-center"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      minHeight: "24px",
                    }}>
                    {nodule.location || "N/A"}
                  </div>
                  <div
                    className="text-left flex items-center"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      minHeight: "24px",
                    }}>
                    Solid
                  </div>
                </div>

                {/* Impression */}
                <div className="mt-2 font-semibold px-2 py-1 text-left bg-[#1D4A72] text-white">
                  IMPRESSION
                </div>
                <div className="mb-2 px-2 py-2 text-left">
                  {nodule.note || "Nodule detected."}
                </div>

                {/* Lung-RADS */}
                <div className="font-semibold px-2 py-1 text-left bg-[#1D4A72] text-white">
                  LUNGRADS ASSESSMENT
                </div>
                <div className="px-2 py-2 pb-3 text-left">
                  {nodule.lung_rads_score || "Not specified"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* LOCATION/SNAPSHOTS SECTION - Wrapper with header and subblocks */}
      <div className="border border-black mt-4" data-pdf-block>
        <div
          className="bg-[#D9E7F4] font-semibold p-2 border-b border-black"
          data-pdf-header>
          LOCATION
        </div>

        {isGenerating && (
          <div
            className="p-4 text-sm text-gray-700"
            data-pdf-subblock
            style={{ padding: "16px" }}>
            Generating snapshots…
          </div>
        )}

        {!isGenerating && snapshotErrors.length > 0 && (
          <div
            className="p-4 text-xs text-red-700"
            data-pdf-subblock
            style={{ padding: "16px" }}>
            {snapshotErrors.map((e, i) => (
              <div key={i}>• {e}</div>
            ))}
          </div>
        )}

        {!isGenerating && snapshots.length > 0 && (
          <div className="p-6" style={{ padding: "24px" }}>
            {snapshots.map((s, i) => (
              <div
                key={s.noduleId || i}
                className="grid grid-cols-5 gap-6 pb-6 mb-6 border-b border-black/10 last:border-0"
                data-pdf-subblock>
                {/* Image */}
                <div className="col-span-3">
                  <div className="rounded-md overflow-hidden border border-black/10">
                    <img
                      src={s.dataUrl}
                      alt={`Nodule ${i + 1}`}
                      className="block w-full h-auto"
                      style={{ maxWidth: 560 }}
                    />
                  </div>
                </div>

                {/* Details */}
                <div className="col-span-2 text-sm leading-relaxed">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between border-b border-black/10 pb-2">
                      <span className="text-gray-700">Nodule</span>
                      <span className="font-semibold">#{i + 1}</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-black/10 pb-2">
                      <span className="text-gray-700">Diameter</span>
                      <span className="font-medium">
                        {fmt(s.fields?.diameterMm)} mm
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-black/10 pb-2">
                      <span className="text-gray-700">Volume</span>
                      <span className="font-medium">
                        {fmt(s.fields?.volumeMm3)} mm³
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-black/10 pb-2">
                      <span className="text-gray-700">Location</span>
                      <span className="font-medium">
                        {s.fields?.location || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-black/10 pb-2">
                      <span className="text-gray-700">Lung-RADS</span>
                      <span className="font-medium">
                        {s.fields?.lungRads || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-black/10 pb-2">
                      <span className="text-gray-700">Type</span>
                      <span className="font-medium">
                        {s.fields?.type || "N/A"}
                      </span>
                    </div>

                    {s.fields?.note && (
                      <div className="border-b border-black/10 pb-2">
                        <div className="text-gray-700 mb-1">Note</div>
                        <div className="font-normal">{s.fields.note}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!isGenerating &&
          snapshots.length === 0 &&
          snapshotErrors.length === 0 && (
            <div
              className="p-4 text-xs text-gray-600"
              data-pdf-subblock
              style={{ padding: "16px" }}>
              No snapshots to display.
            </div>
          )}
      </div>

      {/* REPORT SECTION - Force new page and treat each field as a subblock */}
      <div
        className="border border-black mt-4"
        data-pdf-block
        data-pdf-break-before="always">
        <div
          className="bg-[#D9E7F4] font-semibold p-2 border-b border-black"
          data-pdf-header>
          REPORT
        </div>

        <div className="p-4 space-y-4">
          {REPORT_SECTIONS.map(({ key, label }) => {
            const value = reportFields?.[key];
            const hasVal = value != null && String(value).trim().length > 0;

            return (
              <div
                key={key}
                className="border border-black/10 rounded"
                data-pdf-subblock>
                <div className="px-3 py-2 bg-[#F3F6FA] font-semibold border-b border-black/10">
                  {label}
                </div>
                <div className="px-3 py-3 text-sm whitespace-pre-wrap">
                  {hasVal ? value : "—"}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PDFPreview;
